import type { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { assertNever } from 'owa-assert';
import { ComposeUniqueState, IAttachmentLocalComputerFilePicker } from 'owa-attachment-filepicker';
import { AttachmentMenuInfo, attachmentMultiMenuEntry } from 'owa-attachment-multi-menu-actions';
import { isFeatureEnabled } from 'owa-feature-flags';
import getComposeUniqueState from 'owa-mail-compose-actions/lib/utils/getComposeUniqueState';
import type { ComposeViewState } from 'owa-mail-compose-store';
import getAttachmentPolicy from 'owa-session-store/lib/utils/getAttachmentPolicy';
import isSmimeEnabledInViewState from 'owa-smime/lib/utils/isSmimeEnabledInViewState';
import { logUserAction, SurfaceActionItem } from 'owa-surface-actions';
import type * as React from 'react';
import AttachmentMenuEntrySource from '../types/AttachmentMenuEntrySource';
import type { AttachmentMultiMenuMap } from '../types/AttachmentMultiMenuMap';
import { openCloudFilePicker, openLocalFilePicker } from './openFilePicker';
import { lazyGetAttachmentSuggestionsTraceId } from 'owa-attachment-suggestions';

export enum AttachButton {
    COMPUTER_ATTACHMENT_KEY = 'addMyComputerAttachment',
    ONLINE_LOCATION_KEY = 'addOnlineLocationAttachment',
    UPLOAD_AND_SHARE_FROM_CLOUD_KEY = 'addUploadAndShareFromCloud',
}

/**
 * To add a button, update the AttachButton enum, update getDefaultButtonsList, then update this method.
 */
export default function getAttachmentCommandItems(
    getAttachmentLocalComputerFilePicker: () => IAttachmentLocalComputerFilePicker | null,
    getAttachmentLocalComputerToCloudFilePicker: () => IAttachmentLocalComputerFilePicker | null,
    getLocalComputerFileToLinkPicker: () => IAttachmentLocalComputerFilePicker | null,
    viewState: ComposeViewState,
    targetWindow: Window
): IContextualMenuItem[] {
    const attachmentMultiMenuInfo: AttachmentMultiMenuMap<{
        actionName: string;
        onClick: () => void;
    }> = {
        computer: {
            actionName: 'computerAttach',
            onClick: () =>
                openLocalFilePicker(
                    viewState,
                    getAttachmentLocalComputerFilePicker,
                    'AttachLocalComputerClicked'
                ),
        },
        onlineLocation: {
            actionName: 'onlineAttach',
            onClick: () => {
                openCloudFilePicker(viewState, targetWindow);
            },
        },
        uploadAndShareFromCloud: {
            actionName: 'computerToCloudAttach',
            onClick: () =>
                openLocalFilePicker(
                    viewState,
                    isFeatureEnabled('doc-deprecateCloudy-preview') ||
                        isFeatureEnabled('doc-deprecateCloudy-preview-thumbnailView')
                        ? getLocalComputerFileToLinkPicker
                        : getAttachmentLocalComputerToCloudFilePicker,
                    'AttachUploadAndSharedClicked'
                ),
        },
    };

    const referenceAttachmentsSupported =
        getComposeUniqueState(viewState) === ComposeUniqueState.None;
    const isSmimeCompose = isSmimeEnabledInViewState(viewState.smimeViewState);
    const defaultButtonTypes: AttachButton[] = isSmimeCompose
        ? getSmimeAttachButtonsList()
        : getDefaultButtonsList(referenceAttachmentsSupported, !!viewState.attachmentWell?.groupId);

    const defaultButtons: IContextualMenuItem[] = defaultButtonTypes.map<IContextualMenuItem>(
        defaultButtonType => {
            switch (defaultButtonType) {
                case AttachButton.COMPUTER_ATTACHMENT_KEY:
                    return createAttachmentContextMenuItem(
                        attachmentMultiMenuEntry.computer,
                        AttachButton.COMPUTER_ATTACHMENT_KEY,
                        attachmentMultiMenuInfo.computer.actionName,
                        attachmentMultiMenuInfo.computer.onClick,
                        viewState
                    );
                case AttachButton.UPLOAD_AND_SHARE_FROM_CLOUD_KEY:
                    return createAttachmentContextMenuItem(
                        attachmentMultiMenuEntry.uploadAndShareFromCloud,
                        AttachButton.UPLOAD_AND_SHARE_FROM_CLOUD_KEY,
                        attachmentMultiMenuInfo.uploadAndShareFromCloud.actionName,
                        attachmentMultiMenuInfo.uploadAndShareFromCloud.onClick,
                        viewState
                    );
                case AttachButton.ONLINE_LOCATION_KEY:
                    return createAttachmentContextMenuItem(
                        attachmentMultiMenuEntry.onlineLocation,
                        AttachButton.ONLINE_LOCATION_KEY,
                        attachmentMultiMenuInfo.onlineLocation.actionName,
                        attachmentMultiMenuInfo.onlineLocation.onClick,
                        viewState
                    );
                default:
                    return assertNever(defaultButtonType);
            }
        }
    );

    return defaultButtons;
}

function createAttachmentContextMenuItem(
    menuInfo: AttachmentMenuInfo,
    attachButtonType: AttachButton,
    actionName: string,
    onClick: () => void,
    composeViewState: ComposeViewState
): IContextualMenuItem {
    const aria = menuInfo.ariaLabel?.();

    return {
        key: attachButtonType,
        name: menuInfo.text(),
        ariaLabel: aria,
        title: aria,
        iconProps: menuInfo.iconProps,
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (127,9): Type '(event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>, item?: SurfaceActionItem | undefined) => void' is not assignable to type '(ev?: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement, MouseEvent> | undefined, item?: IContextualMenuItem | undefined) => boolean | void'.
        // @ts-expect-error
        onClick: (
            event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
            item?: SurfaceActionItem
        ) => {
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2345 (134,61): Argument of type 'SurfaceActionItem | undefined' is not assignable to parameter of type 'SurfaceActionItem'.
            // @ts-expect-error
            logUserAction(true /* isCompose */, actionName, item);
            onClick();
            lazyGetAttachmentSuggestionsTraceId.import().then(getAttachmentSuggestionsTraceId => {
                const traceId = getAttachmentSuggestionsTraceId(composeViewState.referenceItemId);
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2345 (141,70): Argument of type 'string | null' is not assignable to parameter of type 'string'.
                // @ts-expect-error
                menuInfo.logUsage(AttachmentMenuEntrySource.ToolBar, traceId);
            });
        },
    };
}

/**
 * Returns a list of button names (email/cloud/local etc) shown in the Attach button.
 * Used for logging. Needs to be consistent with getAttachmentCommandItems method.
 */
export function getDefaultButtonsList(
    referenceAttachmentsSupported: boolean,
    isGroupsScenario?: boolean
): AttachButton[] {
    const defaultButtons: AttachButton[] = [];
    const referenceAttachmentsEnabled =
        referenceAttachmentsPolicyEnabled() && referenceAttachmentsSupported;

    if (classicAttachmentsPolicyEnabled()) {
        defaultButtons.push(AttachButton.COMPUTER_ATTACHMENT_KEY);
    }
    if (classicAttachmentsPolicyEnabled() || referenceAttachmentsEnabled) {
        defaultButtons.push(AttachButton.ONLINE_LOCATION_KEY);
    }

    if (
        attachmentMultiMenuEntry.uploadAndShareFromCloud.isEnabled(isGroupsScenario) &&
        referenceAttachmentsEnabled
    ) {
        defaultButtons.push(AttachButton.UPLOAD_AND_SHARE_FROM_CLOUD_KEY);
    }

    return defaultButtons;
}

/**
 * Returns the list of buttons that needed to be shown in attach menu
 * in case of a S/MIME compose.
 */
export function getSmimeAttachButtonsList(): AttachButton[] {
    return [AttachButton.COMPUTER_ATTACHMENT_KEY];
}

function classicAttachmentsPolicyEnabled(): boolean {
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2322 (188,5): Type 'boolean | undefined' is not assignable to type 'boolean'.
    // @ts-expect-error
    return getAttachmentPolicy().ClassicAttachmentsEnabled;
}

function referenceAttachmentsPolicyEnabled(): boolean {
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2322 (195,5): Type 'boolean | undefined' is not assignable to type 'boolean'.
    // @ts-expect-error
    return getAttachmentPolicy().ReferenceAttachmentsEnabled;
}
